import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  Chip,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Divider,
  Stack,
  LinearProgress,
  Alert,
  Card,
  CardContent,
  CircularProgress,
  Snackbar,
  Autocomplete,
} from "@mui/material";
import {
  FilterList,
  CalendarToday,
  ArrowDropDown,
  Payment,
  Receipt,
  Download,
  Visibility,
  CreditCard,
  CheckCircle,
  Warning,
  Info,
  Refresh,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import URLs from "constants/urls";
import Cookie from "js-cookie";
import { useMaterialUIController } from "context";
import { tr } from "date-fns/locale";

// Modern styled components
const PrimaryButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(135deg, #FF6B35 0%, #FFA62E 100%)",
  color: "#FFFFFF",
  fontWeight: 600,
  fontSize: "14px",
  padding: "10px 24px",
  borderRadius: "8px",
  textTransform: "none",
  boxShadow: "0 4px 12px rgba(255, 107, 53, 0.3)",
  transition: "all 0.3s ease",
  "&:hover": {
    background: "linear-gradient(135deg, #E55A2E 0%, #E59529 100%)",
    transform: "translateY(-1px)",
    boxShadow: "0 6px 16px rgba(255, 107, 53, 0.4)",
  },
  "&.Mui-disabled": {
    background: "#e0e0e0",
    color: "#9e9e9e",
  },
}));

const SecondaryButton = styled(Button)(({ theme }) => ({
  border: "1px solid #FF6B35",
  color: "#FF6B35",
  fontWeight: 600,
  fontSize: "13px",
  padding: "8px 20px",
  borderRadius: "8px",
  textTransform: "none",
  "&:hover": {
    backgroundColor: "rgba(255, 107, 53, 0.04)",
    borderWidth: "1px",
  },
}));

const PrimaryTypography = styled(Typography)({
  color: "#1a1a1a",
  fontWeight: 600,
  fontSize: "14px",
});

const SecondaryTypography = styled(Typography)({
  color: "#666666",
  fontSize: "13px",
  fontWeight: 400,
});

// Modern Card with glass effect
const ModernCard = styled(Card)(({ theme }) => ({
  background: "#FFFFFF",
  borderRadius: "12px",
  border: "1px solid #f0f0f0",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
  },
}));

const CtcVoucher = () => {
  const [controller] = useMaterialUIController(); // Get context state
  const { user } = controller; // Destructure user
  const [payPeriods, setPayPeriods] = useState([]);
  const [selectedPayPeriod, setSelectedPayPeriod] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPeriods, setLoadingPeriods] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [confirmationDialog, setConfirmationDialog] = useState(false);
  const [selectedPayables, setSelectedPayables] = useState({});
  const [selectedBank, setSelectedBank] = useState("");
  const [bankAccounts, setBankAccounts] = useState([]);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentDate] = useState(new Date().toISOString().split("T")[0]);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const jwt_token = Cookie.get("jwt_token");

  // Load pay periods on component mount
  useEffect(() => {
    loadPayPeriods();
    loadBankAccounts();
  }, []);

  // Load employees when pay period changes
  useEffect(() => {
    if (selectedPayPeriod?.payPeriodId) {
      loadEmployees(selectedPayPeriod.payPeriodId);
    }
  }, [selectedPayPeriod]);

  const loadPayPeriods = async () => {
    setLoadingPeriods(true);
    try {
      const response = await fetch(URLs.hrmsGetPayPeriods, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setPayPeriods(data);
      if (data.length > 0) {
        setSelectedPayPeriod(data[0]);
      }
    } catch (error) {
      console.error("Error loading pay periods:", error);
      showSnackbar("Failed to load pay periods", "error");
    } finally {
      setLoadingPeriods(false);
    }
  };

  const loadBankAccounts = async () => {
    try {
      const url = `${URLs.headOfficeBanksAcc}/${user.companyId}`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setBankAccounts(data);
    } catch (error) {
      showSnackbar("Failed to load bank accounts", "error");
    }
  };

  const loadEmployees = async (payPeriodId) => {
    setLoadingEmployees(true);
    try {
      const url = `${URLs.ctcPmtVouchersGetUrl}/${payPeriodId}`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Transform API data to match component structure
      const transformedData = data.map((emp, index) => {
        const totalPayable = emp.payableComponents.reduce((sum, comp) => sum + comp.amount, 0);

        // Calculate paid amount based on paymentStatus
        const paidAmount = emp.payableComponents.reduce(
          (sum, comp) => sum + (comp.paymentStatus === "Y" ? comp.amount : 0),
          0
        );

        return {
          id: emp.employeeId || index + 1,
          name: emp.employeeName,
          employeeId: emp.employeeId,
          department: "Not Provided",
          paymentId: emp.payRollId,
          payableComponents: emp.payableComponents.map((comp, compIndex) => ({
            id: comp.ledgerId || compIndex + 1,
            type: comp.type,
            amount: comp.amount,
            payable: comp.paymentStatus === "N", // Now based on paymentStatus
            paid: comp.paymentStatus === "Y" ? comp.amount : 0, // Full amount if already paid
            dueDate: getDueDate(comp.type),
            ledgerId: comp.ledgerId,
            paymentStatus: comp.paymentStatus, // Keep original status
          })),
          totalPayable: totalPayable,
          paid: paidAmount,
          status: paidAmount === totalPayable ? "PAID" : "PENDING",
        };
      });

      setEmployees(transformedData);
      setSelectedEmployees([]);
      setSelectedPayables({});
    } catch (error) {
      console.error("Error loading employees:", error);
      showSnackbar("Failed to load employee data", "error");
    } finally {
      setLoadingEmployees(false);
    }
  };

  // Helper function to determine due date based on component type
  const getDueDate = (componentType) => {
    const today = new Date();
    const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);

    if (componentType.includes("TDS")) {
      return new Date(today.getFullYear(), today.getMonth() + 1, 7).toISOString().split("T")[0]; // 7th of next month
    } else if (componentType.includes("PF")) {
      return new Date(today.getFullYear(), today.getMonth() + 1, 15).toISOString().split("T")[0]; // 15th of next month
    } else if (componentType.includes("Professional Tax")) {
      return new Date(today.getFullYear(), today.getMonth() + 1, 10).toISOString().split("T")[0]; // 10th of next month
    } else if (componentType.includes("Salary")) {
      return nextMonth.toISOString().split("T")[0]; // 1st of next month
    } else {
      return nextMonth.toISOString().split("T")[0]; // Default to 1st of next month
    }
  };

  // Calculate totals
  const calculateTotals = () => {
    return employees.reduce(
      (acc, emp) => {
        acc.totalPayable += emp.totalPayable;
        acc.totalPaid += emp.paid;
        acc.balance += emp.totalPayable - emp.paid;
        return acc;
      },
      { totalPayable: 0, totalPaid: 0, balance: 0 }
    );
  };

  const totals = calculateTotals();

  // Handle employee selection
  const handleSelectEmployee = (employeeId) => {
    setSelectedEmployees((prev) => {
      if (prev.includes(employeeId)) {
        return prev.filter((id) => id !== employeeId);
      } else {
        return [...prev, employeeId];
      }
    });
  };

  // Handle payable component selection
  const handleSelectPayable = (employeeId, componentId, payableType) => {
    // Find the employee and component first
    const employee = employees.find((e) => e.id === employeeId);
    const component = employee?.payableComponents.find((c) => c.id === componentId);

    // Don't allow selection if component is already paid (paymentStatus === "Y")
    if (component?.paymentStatus === "Y") {
      return; // Exit early, do nothing
    }

    setSelectedPayables((prev) => {
      const employeePayables = prev[employeeId] || [];
      const existing = employeePayables.find((p) => p.componentId === componentId);

      if (existing) {
        // Deselect - remove from selection
        const updated = employeePayables.filter((p) => p.componentId !== componentId);
        return { ...prev, [employeeId]: updated };
      } else {
        // Select - add to selection
        const updated = [
          ...employeePayables,
          {
            componentId,
            payableType,
            amount: component.amount - component.paid,
            paystrucId: componentId,
            ledgerId: component.ledgerId,
          },
        ];
        return { ...prev, [employeeId]: updated };
      }
    });
  };
  // Calculate selected amount
  const calculateSelectedAmount = () => {
    let total = 0;
    Object.values(selectedPayables).forEach((payables) => {
      payables.forEach((p) => {
        total += p.amount;
      });
    });
    return total;
  };

  // Open payment confirmation
  const handleOpenPayment = () => {
    const amount = calculateSelectedAmount();
    if (amount > 0) {
      setPaymentAmount(amount);
      setConfirmationDialog(true);
    } else {
      showSnackbar("Please select payable components", "warning");
    }
  };

  // Prepare payment data
  const preparePaymentData = () => {
    const paymentData = Object.keys(selectedPayables).map((employeeId) => {
      return {
        khBankId: selectedBank, // bankAccountId → khBankId
        employeeId: parseInt(employeeId),
        companyId: 0,
        payPeriodId: selectedPayPeriod.payPeriodId,
        transactions: selectedPayables[employeeId].map((p) => ({
          ledgerId: p.ledgerId,
          amount: p.amount,
        })),
      };
    });

    return paymentData;
  };

  // Process payment
  const handleProcessPayment = async () => {
    if (!selectedBank) {
      showSnackbar("Please select a bank account", "error");
      return;
    }

    setConfirmationDialog(false);
    setPaymentDialog(true);

    try {
      const paymentData = preparePaymentData();
      console.log("Payment Data to be sent:", paymentData);

      const response = await fetch(URLs.createPaymentVoucher, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
        body: JSON.stringify(paymentData),
      });
      const message = await response.text();

      if (!response.ok) {
        showSnackbar(message || "Failed to process payment", "error");
        setPaymentDialog(false);
        return;
      }

      showSnackbar(message || "Payment processed successfully!", "success");

      // Refresh data
      if (selectedPayPeriod) {
        loadEmployees(selectedPayPeriod.payPeriodId);
      }

      // Reset state
      setSelectedEmployees([]);
      setSelectedPayables({});
      setSelectedBank("");
      setPaymentDialog(false);
    } catch (error) {
      console.error("Error processing payment:", error);
      showSnackbar("Network error. Please try again later.", "error");
      setPaymentDialog(false);
    }
  };

  // Get status chip color
  const getStatusColor = (status) => {
    switch (status) {
      case "PAID":
        return "success";
      case "PARTIAL":
        return "warning";
      case "PENDING":
        return "error";
      default:
        return "default";
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  return (
    <DashboardLayout>
      <Box sx={{ p: 2, bgcolor: "transparent", height: "100%" }}>
        {/* Header Section */}
        <ModernCard sx={{ p: 3, mb: 3 }}>
          <Grid container alignItems="center" justifyContent="space-between" spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography
                variant="h5"
                sx={{ color: "#1a1a1a", fontWeight: 600, fontSize: "18px", mb: 1 }}
              >
                Payroll Payments
              </Typography>
              <Typography sx={{ color: "#666666", fontSize: "13px" }}>
                Process salary and statutory payments for employees
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box display="flex" gap={1} alignItems="center" justifyContent="flex-end">
                <Autocomplete
                  sx={{ width: 250 }}
                  options={payPeriods}
                  value={selectedPayPeriod}
                  onChange={(event, newValue) => {
                    setSelectedPayPeriod(newValue);
                  }}
                  getOptionLabel={(option) => option.payPeriodName}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Pay Period"
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <CalendarToday sx={{ mr: 1, color: "#FF6B35", fontSize: "16px" }} />
                        ),
                      }}
                    />
                  )}
                  loading={loadingPeriods}
                  disabled={loadingPeriods}
                />
                <Tooltip title="Refresh">
                  <IconButton
                    onClick={() =>
                      selectedPayPeriod && loadEmployees(selectedPayPeriod.payPeriodId)
                    }
                    size="small"
                  >
                    <Refresh sx={{ fontSize: "18px" }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </ModernCard>

        {/* Employees Table */}
        {loadingEmployees ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress size={40} />
          </Box>
        ) : (
          <ModernCard>
            <TableContainer>
              <Table>
                <TableBody>
                  <TableRow sx={{ bgcolor: "#f9f9f9" }}>
                    <TableCell padding="checkbox" sx={{ width: 50 }}>
                      <Checkbox
                        indeterminate={
                          selectedEmployees.length > 0 &&
                          selectedEmployees.length < employees.length
                        }
                        checked={
                          selectedEmployees.length === employees.length && employees.length > 0
                        }
                        onChange={() => {
                          if (selectedEmployees.length === employees.length) {
                            setSelectedEmployees([]);
                            setSelectedPayables({});
                          } else {
                            setSelectedEmployees(employees.map((e) => e.id));
                          }
                        }}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: "13px", fontWeight: 600, py: 1.5 }}>
                      Employee Details
                    </TableCell>
                    <TableCell sx={{ fontSize: "13px", fontWeight: 600, py: 1.5 }}>
                      Total Payable
                    </TableCell>
                    <TableCell sx={{ fontSize: "13px", fontWeight: 600, py: 1.5 }}>Paid</TableCell>
                    <TableCell sx={{ fontSize: "13px", fontWeight: 600, py: 1.5 }}>
                      Balance
                    </TableCell>
                    <TableCell sx={{ fontSize: "13px", fontWeight: 600, py: 1.5 }}>
                      Status
                    </TableCell>
                  </TableRow>
                  {employees.map((employee) => {
                    const isSelected = selectedEmployees.includes(employee.id);
                    const balance = employee.totalPayable - employee.paid;

                    return (
                      <React.Fragment key={employee.id}>
                        <TableRow
                          hover
                          selected={isSelected}
                          sx={{
                            bgcolor: isSelected ? "rgba(255, 107, 53, 0.04)" : "inherit",
                            transition: "background-color 0.2s ease",
                          }}
                        >
                          <TableCell padding="checkbox" sx={{ py: 1 }}>
                            <Checkbox
                              checked={isSelected}
                              onChange={() => handleSelectEmployee(employee.id)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell sx={{ py: 1.5 }}>
                            <Box>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontSize: "13px", fontWeight: 600 }}
                              >
                                {employee.name}
                              </Typography>
                              <Typography sx={{ color: "#666666", fontSize: "12px" }}>
                                ID: {employee.employeeId}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ py: 1.5 }}>
                            <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
                              ₹{employee.totalPayable.toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 1.5 }}>
                            <Typography
                              color="success.main"
                              sx={{ fontSize: "13px", fontWeight: 600 }}
                            >
                              ₹{employee.paid.toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 1.5 }}>
                            <Typography
                              color={balance > 0 ? "error.main" : "inherit"}
                              sx={{ fontSize: "13px", fontWeight: 600 }}
                            >
                              ₹{balance.toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 1.5 }}>
                            <Chip
                              label={employee.status}
                              color={getStatusColor(employee.status)}
                              size="small"
                              sx={{ fontSize: "12px", fontWeight: 500, height: 24 }}
                            />
                          </TableCell>
                        </TableRow>

                        {/* Expandable Payable Components Row */}
                        {isSelected && (
                          <TableRow>
                            <TableCell colSpan={7} sx={{ p: 0, borderTop: "1px solid #f0f0f0" }}>
                              <Box sx={{ p: 2, bgcolor: "#f9f9f9" }}>
                                <Typography
                                  variant="subtitle2"
                                  gutterBottom
                                  sx={{ fontSize: "14px", fontWeight: 600, mb: 2 }}
                                >
                                  Select Payable Components for {employee.name}
                                </Typography>
                                <Grid container spacing={1.5}>
                                  {employee.payableComponents.map((component) => {
                                    const isComponentSelected = selectedPayables[employee.id]?.find(
                                      (p) => p.componentId === component.id
                                    );
                                    const isFullyPaid = component.amount === component.paid;
                                    const remainingAmount = component.amount - component.paid;

                                    return (
                                      <Grid item xs={12} md={4} key={component.id}>
                                        <ModernCard
                                          sx={{
                                            border: `5px solid ${
                                              isComponentSelected
                                                ? "orange"
                                                : component.paymentStatus === "Y"
                                                ? "#509652"
                                                : "#f0f0f0"
                                            }`,
                                            cursor:
                                              component.paymentStatus === "Y"
                                                ? "default"
                                                : "pointer",
                                            opacity: component.paymentStatus === "Y" ? 0.8 : 1,
                                            position: "relative",
                                            "&:hover": {
                                              borderColor:
                                                component.paymentStatus === "Y"
                                                  ? "#4caf50"
                                                  : "#FF6B35",
                                            },
                                          }}
                                          onClick={() => {
                                            if (component.paymentStatus !== "Y") {
                                              handleSelectPayable(
                                                employee.id,
                                                component.id,
                                                component.type
                                              );
                                            }
                                          }}
                                        >
                                          {/* Tick mark overlay for paid components */}
                                          {component.paymentStatus === "Y" && (
                                            <Box
                                              sx={{
                                                position: "absolute",
                                                top: "50%",
                                                left: "50%",
                                                transform: "translate(-50%, -50%)",
                                                zIndex: 2,
                                                bgcolor: "#4caf50",
                                                borderRadius: "50%",
                                                width: 50,
                                                height: 50,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                boxShadow: 3,
                                              }}
                                            >
                                              <CheckCircle
                                                fontSize="large"
                                                sx={{ color: "#ffffff" }}
                                              />
                                            </Box>
                                          )}

                                          <CardContent sx={{ p: 1.5, position: "relative" }}>
                                            <Grid
                                              container
                                              alignItems="center"
                                              justifyContent="space-between"
                                            >
                                              <Grid item xs={8}>
                                                <Typography
                                                  variant="body2"
                                                  sx={{
                                                    fontSize: "13px",
                                                    fontWeight: 500,
                                                    textDecoration:
                                                      component.paymentStatus === "Y"
                                                        ? "line-through"
                                                        : "none",
                                                    color:
                                                      component.paymentStatus === "Y"
                                                        ? "#666"
                                                        : "inherit",
                                                  }}
                                                >
                                                  {component.type}
                                                </Typography>
                                              </Grid>
                                              <Grid item xs={4}>
                                                <Stack alignItems="flex-end" spacing={0.25}>
                                                  <Typography
                                                    variant="body2"
                                                    sx={{
                                                      fontSize: "13px",
                                                      fontWeight: 600,
                                                      color:
                                                        component.paymentStatus === "Y"
                                                          ? "#4caf50"
                                                          : "inherit",
                                                    }}
                                                  >
                                                    ₹{component.amount.toLocaleString()}
                                                  </Typography>
                                                  {component.paymentStatus === "Y" && (
                                                    <Typography
                                                      variant="caption"
                                                      sx={{
                                                        color: "#4caf50",
                                                        fontSize: "10px",
                                                        fontWeight: 600,
                                                      }}
                                                    >
                                                      Paid
                                                    </Typography>
                                                  )}
                                                </Stack>
                                              </Grid>
                                            </Grid>
                                            <Box sx={{ mt: 1 }}>
                                              {component.paymentStatus === "Y" ? (
                                                <Chip
                                                  icon={<CheckCircle sx={{ fontSize: "14px" }} />}
                                                  label="Fully Paid"
                                                  color="success"
                                                  size="small"
                                                  sx={{
                                                    width: "100%",
                                                    fontSize: "11px",
                                                    height: 22,
                                                    bgcolor: "#e8f5e9",
                                                  }}
                                                />
                                              ) : (
                                                <>
                                                  <LinearProgress
                                                    variant="determinate"
                                                    value={
                                                      component.paid > 0
                                                        ? (component.paid / component.amount) * 100
                                                        : 0
                                                    }
                                                    sx={{
                                                      height: 6,
                                                      borderRadius: 3,
                                                      mb: 0.5,
                                                      bgcolor: "#e0e0e0",
                                                      "& .MuiLinearProgress-bar": {
                                                        bgcolor: isComponentSelected
                                                          ? "#FF6B35"
                                                          : "#f0f0f0",
                                                      },
                                                    }}
                                                  />
                                                </>
                                              )}
                                            </Box>
                                          </CardContent>
                                        </ModernCard>
                                      </Grid>
                                    );
                                  })}
                                </Grid>
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </ModernCard>
        )}

        {/* Action Bar */}
        {selectedEmployees.length > 0 && (
          <Box
            sx={{
              mt: 3,
              p: 2,
              bgcolor: "white",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            }}
          >
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid item>
                <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
                  {selectedEmployees.length} employee(s) selected
                </Typography>
                <Typography sx={{ color: "#666666", fontSize: "12px" }}>
                  Total payable: ₹{calculateSelectedAmount().toLocaleString()}
                </Typography>
              </Grid>
              <Grid item>
                <Stack direction="row" spacing={1}>
                  <SecondaryButton
                    onClick={() => {
                      setSelectedEmployees([]);
                      setSelectedPayables({});
                    }}
                  >
                    Clear Selection
                  </SecondaryButton>
                  <PrimaryButton
                    startIcon={<Payment sx={{ fontSize: "18px" }} />}
                    onClick={handleOpenPayment}
                    disabled={calculateSelectedAmount() === 0}
                  >
                    Process Payment
                  </PrimaryButton>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Confirmation Dialog */}
        <Dialog
          open={confirmationDialog}
          onClose={() => setConfirmationDialog(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: "12px" } }}
        >
          <DialogTitle sx={{ py: 2 }}>
            <Typography variant="h6" sx={{ fontSize: "16px", fontWeight: 600 }}>
              Confirm Payment
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Alert
              severity="info"
              sx={{
                mb: 2,
                borderRadius: "8px",
                fontSize: "13px",
                "& .MuiAlert-icon": { fontSize: "20px" },
              }}
            >
              Review payment details before proceeding.
            </Alert>

            <Box sx={{ mb: 2 }}>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{ fontSize: "14px", fontWeight: 600 }}
              >
                Payment Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <ModernCard sx={{ p: 1.5 }}>
                    <Typography sx={{ color: "#666666", fontSize: "12px" }}>
                      Total Amount
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ color: "#FF6B35", fontSize: "18px", fontWeight: 600 }}
                    >
                      ₹{paymentAmount.toLocaleString()}
                    </Typography>
                  </ModernCard>
                </Grid>
                <Grid item xs={6}>
                  <ModernCard sx={{ p: 1.5 }}>
                    <Typography sx={{ color: "#666666", fontSize: "12px" }}>Employees</Typography>
                    <Typography variant="h6" sx={{ fontSize: "18px", fontWeight: 600 }}>
                      {selectedEmployees.length}
                    </Typography>
                  </ModernCard>
                </Grid>
              </Grid>
            </Box>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Autocomplete
                size="small"
                options={bankAccounts || []}
                getOptionLabel={(option) => option.actledName || ""}
                value={bankAccounts?.find((bank) => bank.actledId === selectedBank) || null}
                onChange={(event, newValue) => {
                  setSelectedBank(newValue ? newValue.actledId : null);
                }}
                isOptionEqualToValue={(option, value) => option.actledId === value.actledId}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Bank Account"
                    sx={{
                      "& .MuiInputLabel-root": { fontSize: "13px" },
                      "& .MuiInputBase-input": { fontSize: "13px" },
                    }}
                  />
                )}
              />
            </FormControl>

            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ fontSize: "14px", fontWeight: 600, mt: 1 }}
            >
              Selected Components
            </Typography>
            {Object.keys(selectedPayables).map((employeeId) => {
              const employee = employees.find((e) => e.id === parseInt(employeeId));
              return (
                <Box
                  key={employeeId}
                  sx={{ mb: 1, p: 1.5, bgcolor: "#f9f9f9", borderRadius: "8px" }}
                >
                  <Typography variant="body2" sx={{ fontSize: "13px", fontWeight: 500 }}>
                    {employee.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#666666", fontSize: "12px" }}>
                    {selectedPayables[employeeId].length} component(s)
                  </Typography>
                </Box>
              );
            })}
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 0 }}>
            <SecondaryButton onClick={() => setConfirmationDialog(false)}>Cancel</SecondaryButton>
            <PrimaryButton
              onClick={handleProcessPayment}
              startIcon={<CheckCircle sx={{ fontSize: "18px" }} />}
            >
              Confirm Payment
            </PrimaryButton>
          </DialogActions>
        </Dialog>

        {/* Payment Processing Dialog */}
        <Dialog
          open={paymentDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: "12px" } }}
        >
          <DialogContent>
            <Box sx={{ textAlign: "center", py: 3 }}>
              <CircularProgress size={60} thickness={4} sx={{ color: "#FF6B35", mb: 2 }} />
              <Typography variant="h6" sx={{ fontSize: "15px", fontWeight: 600, mb: 1 }}>
                Processing Payment...
              </Typography>
              <Typography sx={{ color: "#666666", fontSize: "13px" }}>
                Creating payment vouchers and updating records
              </Typography>
            </Box>
          </DialogContent>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "middle", horizontal: "top" }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ fontSize: "13px", borderRadius: "8px" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Footer */}
        <Box sx={{ mt: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="caption" sx={{ color: "#666666", fontSize: "12px" }}>
            Showing {employees.length} employees for{" "}
            {selectedPayPeriod?.payPeriodName || "selected period"}
          </Typography>
          <Stack direction="row" spacing={1}></Stack>
        </Box>
      </Box>
    </DashboardLayout>
  );
};

export default CtcVoucher;
